import { h, defineComponent, ref, watch } from 'vue';
import * as qs from '@/ui/queryState';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { t } from '@/ui/i18n/i18n';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { useTask } from '@/ui/concerns';
import { SelectOptions } from '@/ui/types';
import { emptyList } from '@/services/api';
import { list as getEntityFolderList, remove as removeEntityFolder } from '@/services/api/resources/entityFolder';
import { EntityFolderFlatList, EntityFolderFlatListRecord } from '@/entities/entityFolder/model';
import { box, column, indexView, loader } from '@/ui/components';
import { useEntityFolderCheck } from '@/ui/pages/entityFolder/useEntityFolderCheck';
import {
  entityFolderLabelFilter,
  userFilter,
  searchFilter,
  entityItemFilter,
} from '@/ui/components/filters/filters-horizontal';
import { tagColumn } from '@/ui/components/table/TableColumn';
import EntityFolderEmpty from '@/ui/pages/entityFolder/EntityFolderEmpty.vue';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { FeatureNames } from '@/ui/components/NewFeature';
import { Tag } from '@/ui/components/Tag';
import { filterListToOptions, sequence } from '@/ui/helpers';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems, remove as removeEntityItem } from '@/services/api/resources/entityItem';
import { filter as getAllLabels } from '@/services/api/resources/entityFolderLabel';
import { filterFormLoader } from '@/ui/components/Loader';
import { RouteName } from '@/ui/routes';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { tooltip } from '@/ui/components/Tooltip';
import { pathSeparator, useTreeView } from '@/ui/concerns/entityFolder/useTreeView';
import CollapseButton from '@/ui/components/entityFolder/treeView/CollapseButton.vue';
import CollapseIcon from '@/ui/components/entityFolder/treeView/CollapseIcon.vue';
import EntityItemIcon from '@/ui/components/entityItem/EntityItemIcon.vue';
import EntityFolderIcon from '@/ui/components/entityFolder/EntityFolderIcon.vue';
import CollapseAllButton from '@/ui/components/entityFolder/treeView/CollapseAllButton.vue';

export const EntityFolderIndexPage = defineComponent({
  name: 'EntityFolderIndexPage',
  setup() {
    if (!hasFeature('entityFolders')) {
      return renderResourceTeaserPage(t('common.entity-folders'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_FOLDERS)) {
      return renderForbiddenPage;
    }

    EventBus.$emit(Events.viewedNewFeature, FeatureNames.ENTITY_FOLDERS);

    const initialLoading = ref(true);

    const search = qs.string('search', '');
    const entityItem = qs.numbers('entityItem', []);
    const user = qs.numbers('user', []);
    const label = qs.numbers('label', []);
    const {
      checkResult,
      performCheck,
    } = useEntityFolderCheck();

    const entityItems = ref<SelectOptions>([]);
    const users = ref<SelectOptions>([]);
    const labels = ref<SelectOptions>([]);
    const collapsed = ref<string[]>([]);
    const deleted = ref<string[]>([]);
    const list = ref<EntityFolderFlatList>(emptyList());
    const {
      convertToList,
      collapseAll,
      expandAll,
      toggleChildren,
      deleteChildren,
      filteredList,
      hasChildren,
      sliceUsersToShow,
    } = useTreeView(list, collapsed, deleted);

    const getList = useTask(
      () => getEntityFolderList({
        search: search.value,
        entityItem: entityItem.value,
        user: user.value,
        label: label.value,
      }),
      entityFolderList => {
        convertToList(entityFolderList.data);
        collapseAll();
        initialLoading.value = false;
      },
    );
    watch([
      search,
      entityItem,
      user,
      label,
    ], getList.run);

    const getFilters = useTask(
      () => sequence(getAllEntityItems(), getAllUsers(), getAllLabels()),
      ([entityItemList, userList, labelList]) => {
        entityItems.value = filterListToOptions(entityItemList);
        users.value = filterListToOptions(userList);
        labels.value = filterListToOptions(labelList);
      },
    );

    const redirectEditRoute = ({ row: listRecord }: { row: EntityFolderFlatListRecord }): RouteName =>
      listRecord.data.entityType ? 'entityItemEdit' : 'entityFolderEdit';

    const remove = (id: number, { row: listRecord }: { row: EntityFolderFlatListRecord }) => {
      return listRecord.data.entityType
        ? removeEntityItem(id)
        : removeEntityFolder(id);
    };

    const handleDelete = ({ entity: listRecord }: { entity: EntityFolderFlatListRecord }) => {
      deleteChildren(listRecord);
    };

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              searchFilter(search),
              entityItemFilter(entityItem, entityItems.value),
              userFilter(user, users.value),
              entityFolderLabelFilter(label, labels.value),
              h(CollapseAllButton, {
                props: {
                  collapseAllFn: () => collapseAll(),
                  expandAllFn: () => expandAll(),
                },
              }),
            ], true, 'entity-folder__filter-form'),
          ],
          [
            box([
              loader('div', getList, [
                h(ResourceTable, {
                  props: {
                    editRoute: redirectEditRoute,
                    list: filteredList.value,
                    deleteFn: remove,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.entity-folders'),
                      singular: t('common.entity-folder'),
                    },
                  },
                  on: {
                    deleted: handleDelete,
                  },
                  scopedSlots: {
                    default: () => {
                      return [
                        column(
                          {
                            field: 'title',
                            label: t('common.title'),
                            width: '60%',
                          }, {
                            default: ({ row: listRecord }: { row: EntityFolderFlatListRecord }) => {
                              return [
                                h(CollapseButton, {
                                  props: {
                                    levelOfNesting: (listRecord.data.path.match(new RegExp(pathSeparator, 'g')) || []).length,
                                    toggleChildrenFn: () => toggleChildren(listRecord),
                                  },
                                }, [
                                  hasChildren(listRecord)
                                    ? h(CollapseIcon, {
                                      props: {
                                        rotation: collapsed.value.includes(listRecord.data.path) ? 0 : 90,
                                      },
                                    })
                                    : null,
                                ]),
                                listRecord.data.entityType ? h(EntityItemIcon) : h(EntityFolderIcon),
                                listRecord.data.title,
                                listRecord.data.entityType ? h(Tag, { props: { type: 'purple' } }, [listRecord.data.entityType.data.title]) : null,
                                listRecord.data.labels.map(item => h(Tag, { class: 'entity-folder__tag' }, [item.label])),
                              ];
                            },
                          },
                        ),
                        tagColumn({
                          field: 'users',
                          label: t('common.users'),
                          width: '35%',
                        }, {
                          default: ({ row: entityFolder }: { row: EntityFolderFlatListRecord }) => {
                            const { usersToShow, remainingUsers } = sliceUsersToShow(entityFolder);

                            if (remainingUsers.length > 0) {
                              return [
                                usersToShow.join(', '),
                                tooltip({
                                  label: remainingUsers.join(', '),
                                  multilined: true,
                                  position: 'is-top',
                                  classes: 'entity-folder__rest-users',
                                }, [`+ ${remainingUsers.length}`], 'label'),
                              ];
                            }

                            return usersToShow.join(', ');
                          },
                        }),
                      ];
                    },
                  },
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(EntityFolderEmpty)]),
      ], false);
    };
  },
});
